import React, { FC, useEffect, useState } from 'react';
import { Col, Form, message, Row, Select } from 'antd';
import moment from 'moment';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, endpoints } from '../../api';
import { routerPath } from '../../router/routerPath';
import {
  FormBlock,
  BlueLayout,
  CustomButton,
  CustomDatePicker,
  CustomGoBackButton,
  FormInput,
  ValidatorFormItem,
  FormItem,
  GridItem,
  CustomMaskInput,
} from '../../styles/styled';
import { applicationNumberRegExp, fioRegExp, fioShortRegExp, phoneNumberRegExp } from '../../utils/patterns';
import BookRegistrationComponent from './components/BookRegistrationComponent';
import { ApplicationRegistrationBlock, NewApplicationContainer } from './styled';
import { ApplicationFormType, PublishersType, RulersType, RulerType, ValidatorRuleType, ValidatorsType } from './types';

const { Option } = Select;

export type NewApplicationPageProps = {
  edit?: boolean;
  newApplication?: ApplicationFormType;
};

const emptyApplicationForm: ApplicationFormType = {
  fio: '',
  email: '',
  phone: '',
  created_at: '',
  date_created: moment().format('YYYY-MM-DD'),
  updated_at: '',
  line_of_textbooks: null,
  registration_number: '',
  date: null,
  right_holder: '',
  is_hidden: false,
  reason_for_refusal: '',
};

export const NewApplicationPage: FC<NewApplicationPageProps> = ({ edit, newApplication }) => {
  const [rulersList, setRulersList] = useState<RulersType>([]);
  const [publishersState, setPublishersState] = useState<PublishersType>([]);
  const [validatorState, setValidatorState] = useState<ValidatorsType>({});
  const [applicationPostedState, setApplicationPosted] = useState<boolean>(false);
  const navigate = useNavigate();
  const today = new Date();
  const { id } = useParams();
  const [currentApplicationId, setCurrentApplicationId] = useState<number | string | null>(null);

  useEffect(() => {
    if (id) setCurrentApplicationId(id);
  }, [id]);

  const registerApplication = () => {
    if (currentApplicationId)
      api
        .put(endpoints.registerApplication(currentApplicationId), currentApplicationId)
        .then(() => {
          message.success('Заявление успешно зарегестрировано');
          navigate(-1);
        })
        .catch(() => message.error('Произошла ошибка'));
  };

  const todayDate =
    today.getFullYear() +
    '-' +
    String(today.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(today.getDate()).padStart(2, '0');

  const todayDateWithTime = `${todayDate}T${String(today.getHours())}:${
    String(today.getMinutes()).length === 1 ? `0${String(today.getMinutes())}` : String(today.getMinutes())
  }:${String(today.getSeconds()).length === 1 ? `0${String(today.getSeconds())}` : String(today.getSeconds())}+00:00`;

  const [applicationForm, setApplicationForm] = useState<ApplicationFormType>({
    ...emptyApplicationForm,
    date: todayDate,
    created_at: todayDateWithTime,
    updated_at: todayDateWithTime,
  });

  useEffect(() => {
    if (edit && newApplication)
      setApplicationForm({
        ...newApplication,
        date: todayDate,
        created_at: todayDateWithTime,
        updated_at: todayDateWithTime,
      });

    if (edit && newApplication?.line_of_textbooks) setRulersList(newApplication.line_of_textbooks);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, newApplication]);

  useEffect(() => {
    setApplicationForm({ ...applicationForm, line_of_textbooks: rulersList });

    // eslint-disable-next-line
  }, [rulersList]);

  const textBooksValidator = (rulers: RulersType) => {
    rulers
      .map((el) => el.textbooks)
      .map((ruler_textbooks, rulerIndex) =>
        ruler_textbooks.map((textbook, textbookIndex) => {
          const rules: ValidatorRuleType = {
            right_holder: textbook?.right_holder?.length > 0,
            fio: textbook?.author?.length > 0,
            subject_general: textbook.subject_general.id > 0,
            subject_area_general: textbook?.subject_general?.subject_area_general?.id > 0,
            title: textbook?.title?.length > 0,
            year: textbook.year.toString().length > 0,
            school_classes: textbook.school_classes.length > 0,
            file_textbook: !!textbook.file_textbook,
            file_guideline: !!textbook.file_guideline,
            text_book_level: !!textbook.text_book_level,
            file_instruction: !!textbook.file_instruction,
          };
          setValidatorState({});

          if (Object.values(rules).includes(false)) {
            setValidatorState((prevState) => {
              return {
                ...prevState,
                [rulerIndex]: prevState[rulerIndex]
                  ? [...prevState[rulerIndex], { index: textbookIndex, ...rules }]
                  : [{ index: textbookIndex, ...rules }],
              };
            });
          }

          return true;
        }),
      );
  };

  const archiveApplicationForm = () => {
    if (
      Object.keys(validatorState).length === 0 &&
      applicationForm?.fio?.length > 0 &&
      applicationForm?.phone?.length > 0 &&
      applicationForm.publisher &&
      applicationNumberRegExp.test(applicationForm.registration_number)
    ) {
      (() => {
        if (id) {
          api
            .put(endpoints.getApplicationById(id), applicationForm)
            .then(() => message.success('Данные успешно обновлены'));

          return;
        }

        const result = { ...applicationForm };

        if (Object.keys(result).includes('key')) delete result.key;

        api
          .post<{ application_id: number }>(endpoints.newApplication(), { ...result, status: { id: 6 } })
          .then((el) => {
            setCurrentApplicationId(el.data.application_id);
            setApplicationPosted(true);
            message.success('Заявление успешно создано и имеет статус "Черновик"');
          })
          .catch(() => message.error('Произошла ошибка'));
      })();
    }
  };

  const removeRulerById = (id: number) => {
    const newRuler = [...rulersList];

    newRuler.splice(id, 1);
    setRulersList(newRuler);
  };

  const updateRuler = (id: number, ruler: RulerType) => {
    const newRulers = [...rulersList];

    newRulers[id] = ruler;
    setRulersList(newRulers);
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<PublishersType>(endpoints.getPublishers(1, 100));

        setPublishersState(response.data.filter((el) => !el.is_archived));
      } catch (err) {}
    })();
  }, []);

  return (
    <NewApplicationContainer>
      <BlueLayout>
        <Link to={routerPath.application}>
          <CustomGoBackButton>{'<'}</CustomGoBackButton>
        </Link>
        Заявление
      </BlueLayout>
      <FormBlock white style={{ padding: '50px 20px', gap: '40px' }}>
        <ApplicationRegistrationBlock>
          <div className="text">Регистрация заявления №</div>
          <ValidatorFormItem
            width="120px"
            validator={
              applicationForm?.registration_number && applicationForm?.registration_number?.length === 0
                ? false
                : !applicationNumberRegExp.test(applicationForm.registration_number)
            }
          >
            <FormInput
              value={applicationForm.registration_number}
              onChange={(e) => setApplicationForm({ ...applicationForm, registration_number: e.target.value })}
              style={{ width: '120px' }}
            />
          </ValidatorFormItem>
          <div className="text">от</div>
          <CustomDatePicker
            value={moment(applicationForm.date_created, 'YYYY-MM-DD')}
            format="DD.MM.YYYY"
            onChange={(date, dateString) =>
              setApplicationForm({
                ...applicationForm,
                date_created: dateString ? dateString.split('.').reverse().join('-') : moment().format('YYYY-MM-DD'),
              })
            }
            placeholder=""
            style={{ width: '150px' }}
          />
        </ApplicationRegistrationBlock>
        <Form name="form" initialValues={applicationForm}>
          <Row gutter={[20, 20]}>
            <Col span={12}>
              <ValidatorFormItem
                validator={
                  applicationForm?.fio?.length > 0
                    ? !(fioShortRegExp.test(applicationForm.fio) || fioRegExp.test(applicationForm.fio))
                    : false
                }
                redstar
              >
                <p>ФИО заявителя</p>
                <FormInput
                  value={applicationForm.fio}
                  onChange={(e) => setApplicationForm({ ...applicationForm, fio: e.target.value })}
                />
              </ValidatorFormItem>
            </Col>
            <Col span={12}>
              <ValidatorFormItem
                validator={
                  applicationForm?.phone?.length === 0 ? false : !phoneNumberRegExp.test(applicationForm.phone)
                }
                redstar
              >
                <p>Телефон</p>

                <CustomMaskInput
                  mask="+7 (999) 999 - 99 - 99"
                  value={applicationForm.phone}
                  onChange={(e) => setApplicationForm({ ...applicationForm, phone: e.target.value })}
                />
              </ValidatorFormItem>
            </Col>
            <Col span={12}>
              <FormItem redstar>
                <p>Издательство</p>
                <Select
                  className="izd-class"
                  value={publishersState.find((el) => el?.id === applicationForm?.publisher?.id)?.title}
                  showSearch
                  placeholder="Не выбрано"
                  onChange={(value, option) =>
                    typeof value === 'string' &&
                    setApplicationForm({
                      ...applicationForm,
                      publisher: { id: parseInt(Object.values(option)[0]) },
                      right_holder: value,
                    })
                  }
                >
                  {publishersState.map((el) => {
                    return (
                      <Option value={el.title} key={el.id}>
                        {el.title}
                      </Option>
                    );
                  })}
                </Select>
              </FormItem>
            </Col>
          </Row>
        </Form>
        {rulersList?.length > 0 &&
          rulersList.map((el, index) => {
            return (
              <BookRegistrationComponent
                validated={validatorState[index]}
                ruler={el}
                textbooks={edit ? el.textbooks : undefined}
                updateRuler={(value) => updateRuler(index, value)}
                removeRulerById={() => removeRulerById(index)}
                key={index}
                id={index}
                publisherId={applicationForm.publisher?.id}
              />
            );
          })}

        <CustomButton
          onClick={() => setRulersList([...rulersList, { textbooks: [] }])}
          lined
          style={{ maxWidth: '300px' }}
        >
          добавить линейку учебников
        </CustomButton>
        <Col span={24}>
          <GridItem align="center" justify="center" direction="row">
            <GridItem width="500px" gap="10px" align="center" justify="center" direction="row">
              <CustomButton
                onClick={() => {
                  archiveApplicationForm();
                  textBooksValidator(rulersList);
                }}
                style={{ margin: 'auto', maxWidth: '300px' }}
              >
                Сохранить как черновик
              </CustomButton>
              <CustomButton
                disable={applicationForm?.status?.id !== 6 && !applicationPostedState}
                disabled={applicationForm?.status?.id !== 6 && !applicationPostedState}
                onClick={() => registerApplication()}
                style={{ margin: 'auto', maxWidth: '300px' }}
              >
                Зарегистрировать
              </CustomButton>
            </GridItem>
          </GridItem>
        </Col>
      </FormBlock>
    </NewApplicationContainer>
  );
};
